// src/components/tools/team-manager/TeamManagerTool.jsx
// Comprehensive Team Management Tool with KPIs, Tasks, Engagements & Scoring
import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye,
  Users, User, Target, CheckSquare, MessageSquare, BarChart3, Calendar,
  Clock, Star, Award, TrendingUp, TrendingDown, ChevronRight, ChevronDown,
  Mail, Phone, MapPin, Briefcase, X, Check, Save, Loader2, Image,
  Upload, UserPlus, ListTodo, ClipboardList, MessageCircle, AlertCircle,
  CheckCircle, Circle, Minus, PlusCircle, Hash, Link2, FileText
} from 'lucide-react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';

// ============================================================
// CONSTANTS
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  MEMBERS_LIST: 'MEMBERS_LIST',
  MEMBER_FORM: 'MEMBER_FORM',
  MEMBER_DETAIL: 'MEMBER_DETAIL',
  TASKS_LIST: 'TASKS_LIST',
  TASK_FORM: 'TASK_FORM',
  ENGAGEMENTS_LIST: 'ENGAGEMENTS_LIST',
  ENGAGEMENT_FORM: 'ENGAGEMENT_FORM',
  KPI_SCORING: 'KPI_SCORING'
};

const TaskStatus = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  REVIEW: 'review',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const TaskPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

const EngagementType = {
  ONE_ON_ONE: 'one_on_one',
  PERFORMANCE_REVIEW: 'performance_review',
  FEEDBACK: 'feedback',
  COACHING: 'coaching',
  RECOGNITION: 'recognition',
  CONCERN: 'concern',
  GENERAL: 'general'
};

const KPIType = {
  NUMERIC: 'numeric',
  PERCENTAGE: 'percentage',
  CURRENCY: 'currency',
  RATING: 'rating',
  BOOLEAN: 'boolean'
};

// ============================================================
// CONTACTS SERVICE
// ============================================================

const fetchWorkContacts = async (bookId) => {
  try {
    const db = getFirestore();
    // Fetch users who have access to this book or are in the organization
    const usersSnap = await getDocs(collection(db, 'pb-users'));
    return usersSnap.docs.map(doc => ({
      userId: doc.id,
      ...doc.data()
    })).filter(u => u.displayName || u.email);
  } catch (error) {
    console.error('Error fetching contacts:', error);
    return [];
  }
};

const uploadMemberPhoto = async (bookId, file) => {
  const storage = getStorage();
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
  const storageRef = ref(storage, `pb-${bookId}/team-members/${fileName}`);
  
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const formatDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const getCurrentWeekKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-W${String(getWeekNumber(now)).padStart(2, '0')}`;
};

const getStatusConfig = (status) => {
  const configs = {
    [TaskStatus.TODO]: { label: 'To Do', color: 'gray', icon: Circle },
    [TaskStatus.IN_PROGRESS]: { label: 'In Progress', color: 'blue', icon: Clock },
    [TaskStatus.REVIEW]: { label: 'In Review', color: 'amber', icon: Eye },
    [TaskStatus.COMPLETED]: { label: 'Completed', color: 'emerald', icon: CheckCircle },
    [TaskStatus.CANCELLED]: { label: 'Cancelled', color: 'red', icon: X }
  };
  return configs[status] || configs[TaskStatus.TODO];
};

const getPriorityConfig = (priority) => {
  const configs = {
    [TaskPriority.LOW]: { label: 'Low', color: 'gray' },
    [TaskPriority.MEDIUM]: { label: 'Medium', color: 'blue' },
    [TaskPriority.HIGH]: { label: 'High', color: 'amber' },
    [TaskPriority.URGENT]: { label: 'Urgent', color: 'red' }
  };
  return configs[priority] || configs[TaskPriority.MEDIUM];
};

const getEngagementConfig = (type) => {
  const configs = {
    [EngagementType.ONE_ON_ONE]: { label: '1-on-1 Meeting', color: 'blue', icon: Users },
    [EngagementType.PERFORMANCE_REVIEW]: { label: 'Performance Review', color: 'purple', icon: BarChart3 },
    [EngagementType.FEEDBACK]: { label: 'Feedback', color: 'amber', icon: MessageSquare },
    [EngagementType.COACHING]: { label: 'Coaching', color: 'emerald', icon: Target },
    [EngagementType.RECOGNITION]: { label: 'Recognition', color: 'yellow', icon: Award },
    [EngagementType.CONCERN]: { label: 'Concern', color: 'red', icon: AlertCircle },
    [EngagementType.GENERAL]: { label: 'General', color: 'gray', icon: MessageCircle }
  };
  return configs[type] || configs[EngagementType.GENERAL];
};

// ============================================================
// UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const Avatar = ({ src, name, size = 'md' }) => {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-lg', xl: 'w-20 h-20 text-2xl' };
  
  if (src) {
    return <img src={src} alt={name} className={`${sizes[size]} rounded-full object-cover`} />;
  }
  
  const initials = name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const colors = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-purple-500', 'bg-cyan-500'];
  const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
  
  return (
    <div className={`${sizes[size]} ${colors[colorIndex]} rounded-full flex items-center justify-center text-white font-semibold`}>
      {initials}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = getPriorityConfig(priority);
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
      {config.label}
    </span>
  );
};

// ============================================================
// MEMBER PROFILE CARD
// ============================================================

const MemberCard = ({ member, onView, onEdit, onDelete, taskCount, avgScore }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all">
      {/* Header with gradient */}
      <div className="h-20 bg-gradient-to-r from-indigo-500 to-purple-500 relative">
        <div className="absolute -bottom-10 left-6">
          <Avatar src={member.photoUrl} name={member.name} size="xl" />
        </div>
      </div>

      {/* Content */}
      <div className="pt-12 pb-5 px-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{member.name}</h3>
            <p className="text-sm text-gray-500">{member.role || 'Team Member'}</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <MoreVertical className="w-4 h-4 text-gray-500" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-36 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-1">
                  <button onClick={() => { onView(member); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button onClick={() => { onEdit(member); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button onClick={() => { if(window.confirm('Delete member?')) onDelete(member.id); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-1 mb-4">
          {member.email && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> {member.email}
            </p>
          )}
          {member.department && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5" /> {member.department}
            </p>
          )}
        </div>

        {/* KPIs Preview */}
        {member.kpis?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">KPIs ({member.kpis.length})</p>
            <div className="flex flex-wrap gap-1">
              {member.kpis.slice(0, 3).map((kpi, i) => (
                <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-lg">
                  {kpi.name}
                </span>
              ))}
              {member.kpis.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-lg">
                  +{member.kpis.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <CheckSquare className="w-4 h-4" />
            <span>{taskCount || 0} tasks</span>
          </div>
          {avgScore !== undefined && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Star className="w-4 h-4 text-amber-500" />
              <span>{avgScore.toFixed(1)}/5</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MEMBER FORM
// ============================================================

const MemberForm = ({ member, bookId, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    photoUrl: '',
    startDate: '',
    notes: '',
    kpis: [],
    userId: null, // Link to pb-users
    ...member
  });

  const [newKpi, setNewKpi] = useState({ name: '', type: KPIType.NUMERIC, target: '', unit: '' });
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  // Load contacts
  useEffect(() => {
    const loadContacts = async () => {
      setLoadingContacts(true);
      const data = await fetchWorkContacts(bookId);
      setContacts(data);
      setLoadingContacts(false);
    };
    loadContacts();
  }, [bookId]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const photoUrl = await uploadMemberPhoto(bookId, file);
      setFormData(prev => ({ ...prev, photoUrl }));
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSelectContact = (contact) => {
    setFormData(prev => ({
      ...prev,
      userId: contact.userId,
      name: contact.displayName || prev.name,
      email: contact.email || prev.email,
      phone: contact.phone || prev.phone,
      photoUrl: contact.photoURL || prev.photoUrl,
      role: contact.role || prev.role,
      department: contact.department || prev.department
    }));
    setShowContactSelector(false);
  };

  const handleAddKpi = () => {
    if (!newKpi.name.trim()) return;
    setFormData(prev => ({
      ...prev,
      kpis: [...prev.kpis, { ...newKpi, id: Date.now() }]
    }));
    setNewKpi({ name: '', type: KPIType.NUMERIC, target: '', unit: '' });
  };

  const handleRemoveKpi = (kpiId) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter(k => k.id !== kpiId)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    onSave(formData);
  };

  const filteredContacts = contacts.filter(c => {
    if (!contactSearch) return true;
    const search = contactSearch.toLowerCase();
    return (c.displayName?.toLowerCase().includes(search) || c.email?.toLowerCase().includes(search));
  });

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {member?.id ? 'Edit Team Member' : 'Add Team Member'}
        </h1>
      </div>

      <div className="space-y-6">
        {/* Import from Contacts */}
        {!member?.id && (
          <div className="bg-indigo-50 rounded-2xl border border-indigo-100 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-medium text-indigo-900">Import from Contacts</p>
                  <p className="text-sm text-indigo-700">Select an existing user to auto-fill details</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowContactSelector(!showContactSelector)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
              >
                {showContactSelector ? 'Close' : 'Select Contact'}
              </button>
            </div>

            {showContactSelector && (
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Search contacts..."
                  className="w-full px-4 py-2 border border-indigo-200 rounded-lg"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {loadingContacts ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <p className="text-sm text-indigo-600 text-center py-4">No contacts found</p>
                  ) : (
                    filteredContacts.map(contact => (
                      <button
                        key={contact.userId}
                        type="button"
                        onClick={() => handleSelectContact(contact)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-indigo-100 text-left"
                      >
                        <Avatar src={contact.photoURL} name={contact.displayName || contact.email} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{contact.displayName || 'No name'}</p>
                          <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                        </div>
                        {formData.userId === contact.userId && (
                          <Check className="w-5 h-5 text-indigo-600" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {formData.userId && (
              <div className="mt-3 flex items-center gap-2 text-sm text-indigo-700">
                <CheckCircle className="w-4 h-4" />
                Linked to user account
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, userId: null }))}
                  className="text-indigo-500 hover:text-indigo-700 underline"
                >
                  Unlink
                </button>
              </div>
            )}
          </div>
        )}

        {/* Profile Photo */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-indigo-600" />
            Profile Photo
          </h2>

          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar src={formData.photoUrl} name={formData.name || 'New Member'} size="xl" />
              {uploadingPhoto && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">or</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.photoUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, photoUrl: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5 text-indigo-600" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role/Position</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g., Software Developer"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g., Engineering"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              placeholder="Any additional notes about this team member..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        {/* KPIs */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Key Performance Indicators (KPIs)
          </h2>

          {formData.kpis.length > 0 && (
            <div className="space-y-2">
              {formData.kpis.map((kpi) => (
                <div key={kpi.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{kpi.name}</p>
                    <p className="text-sm text-gray-500">
                      Type: {kpi.type} | Target: {kpi.target} {kpi.unit}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveKpi(kpi.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="p-4 bg-indigo-50 rounded-xl space-y-3">
            <p className="text-sm font-medium text-indigo-900">Add KPI</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <input
                type="text"
                value={newKpi.name}
                onChange={(e) => setNewKpi(prev => ({ ...prev, name: e.target.value }))}
                placeholder="KPI Name"
                className="col-span-2 px-3 py-2 border border-indigo-200 rounded-lg text-sm"
              />
              <select
                value={newKpi.type}
                onChange={(e) => setNewKpi(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-indigo-200 rounded-lg text-sm"
              >
                <option value={KPIType.NUMERIC}>Numeric</option>
                <option value={KPIType.PERCENTAGE}>Percentage</option>
                <option value={KPIType.CURRENCY}>Currency</option>
                <option value={KPIType.RATING}>Rating (1-5)</option>
                <option value={KPIType.BOOLEAN}>Yes/No</option>
              </select>
              <input
                type="text"
                value={newKpi.target}
                onChange={(e) => setNewKpi(prev => ({ ...prev, target: e.target.value }))}
                placeholder="Target"
                className="px-3 py-2 border border-indigo-200 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newKpi.unit}
                onChange={(e) => setNewKpi(prev => ({ ...prev, unit: e.target.value }))}
                placeholder="Unit (optional, e.g., sales, hours, %)"
                className="flex-1 px-3 py-2 border border-indigo-200 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={handleAddKpi}
                disabled={!newKpi.name.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || uploadingPhoto}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {member?.id ? 'Update' : 'Add'} Member
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// MEMBER DETAIL VIEW
// ============================================================

const MemberDetail = ({ member, tasks, engagements, scores, onEdit, onBack, onAddTask, onAddEngagement }) => {
  const memberTasks = tasks.filter(t => t.assignees?.includes(member.id));
  const memberEngagements = engagements.filter(e => e.memberId === member.id);
  const memberScores = scores.filter(s => s.memberId === member.id);

  const avgScore = memberScores.length > 0
    ? memberScores.reduce((sum, s) => sum + (s.scores?.reduce((a, b) => a + b.score, 0) / (s.scores?.length || 1)), 0) / memberScores.length
    : null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <Avatar src={member.photoUrl} name={member.name} size="lg" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{member.name}</h1>
              <p className="text-gray-500">{member.role || 'Team Member'}</p>
            </div>
          </div>
          <button
            onClick={() => onEdit(member)}
            className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" /> Edit
          </button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{memberTasks.length}</p>
            <p className="text-sm text-gray-500">Tasks</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{memberTasks.filter(t => t.status === TaskStatus.COMPLETED).length}</p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{memberEngagements.length}</p>
            <p className="text-sm text-gray-500">Engagements</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-amber-500">{avgScore ? avgScore.toFixed(1) : '-'}</p>
            <p className="text-sm text-gray-500">Avg Score</p>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact & Info */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Details</h2>
            <div className="space-y-3">
              {member.email && (
                <p className="flex items-center gap-3 text-gray-600">
                  <Mail className="w-4 h-4 text-gray-400" /> {member.email}
                </p>
              )}
              {member.phone && (
                <p className="flex items-center gap-3 text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" /> {member.phone}
                </p>
              )}
              {member.department && (
                <p className="flex items-center gap-3 text-gray-600">
                  <Briefcase className="w-4 h-4 text-gray-400" /> {member.department}
                </p>
              )}
              {member.startDate && (
                <p className="flex items-center gap-3 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" /> Started {formatDate(member.startDate)}
                </p>
              )}
            </div>
          </div>

          {/* KPIs */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">KPIs ({member.kpis?.length || 0})</h2>
            {member.kpis?.length > 0 ? (
              <div className="space-y-2">
                {member.kpis.map(kpi => (
                  <div key={kpi.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-medium text-gray-900">{kpi.name}</p>
                      <p className="text-xs text-gray-500">Target: {kpi.target} {kpi.unit}</p>
                    </div>
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-lg">
                      {kpi.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No KPIs defined</p>
            )}
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Tasks</h2>
            <button
              onClick={() => onAddTask(member)}
              className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add Task
            </button>
          </div>
          {memberTasks.length > 0 ? (
            <div className="space-y-2">
              {memberTasks.slice(0, 5).map(task => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={task.status} />
                    <span className="font-medium text-gray-900">{task.title}</span>
                  </div>
                  <PriorityBadge priority={task.priority} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No tasks assigned</p>
          )}
        </div>

        {/* Recent Engagements */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Engagements</h2>
            <button
              onClick={() => onAddEngagement(member)}
              className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add Engagement
            </button>
          </div>
          {memberEngagements.length > 0 ? (
            <div className="space-y-2">
              {memberEngagements.slice(0, 5).map(eng => {
                const config = getEngagementConfig(eng.type);
                const Icon = config.icon;
                return (
                  <div key={eng.id} className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 text-${config.color}-600`} />
                      <span className="font-medium text-gray-900">{config.label}</span>
                      <span className="text-xs text-gray-400">{formatDate(eng.date)}</span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{eng.notes}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No engagements recorded</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TASK FORM
// ============================================================

const TaskForm = ({ task, members, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignees: [],
    dueDate: '',
    tags: [],
    ...task
  });

  const handleToggleAssignee = (memberId) => {
    setFormData(prev => ({
      ...prev,
      assignees: prev.assignees.includes(memberId)
        ? prev.assignees.filter(id => id !== memberId)
        : [...prev.assignees, memberId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {task?.id ? 'Edit Task' : 'Create Task'}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {Object.values(TaskStatus).map(status => (
                <option key={status} value={status}>{getStatusConfig(status).label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {Object.values(TaskPriority).map(priority => (
                <option key={priority} value={priority}>{getPriorityConfig(priority).label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        {/* Assignees */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {members.map(member => (
              <button
                key={member.id}
                type="button"
                onClick={() => handleToggleAssignee(member.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  formData.assignees.includes(member.id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Avatar src={member.photoUrl} name={member.name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{member.name}</p>
                  <p className="text-xs text-gray-500 truncate">{member.role}</p>
                </div>
                {formData.assignees.includes(member.id) && (
                  <Check className="w-5 h-5 text-indigo-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {task?.id ? 'Update' : 'Create'} Task
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// ENGAGEMENT FORM
// ============================================================

const EngagementForm = ({ engagement, members, tasks, selectedMember, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    memberId: selectedMember?.id || '',
    type: EngagementType.ONE_ON_ONE,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    linkedKpis: [],
    linkedTasks: [],
    followUp: '',
    ...engagement
  });

  const selectedMemberData = members.find(m => m.id === formData.memberId);
  const memberTasks = tasks.filter(t => t.assignees?.includes(formData.memberId));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.memberId || !formData.notes.trim()) {
      alert('Member and notes are required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {engagement?.id ? 'Edit Engagement' : 'Record Engagement'}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        {/* Member Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team Member *</label>
          <select
            value={formData.memberId}
            onChange={(e) => setFormData(prev => ({ ...prev, memberId: e.target.value, linkedKpis: [], linkedTasks: [] }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          >
            <option value="">Select member...</option>
            {members.map(member => (
              <option key={member.id} value={member.id}>{member.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {Object.values(EngagementType).map(type => (
                <option key={type} value={type}>{getEngagementConfig(type).label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes *</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={5}
            placeholder="What was discussed, outcomes, action items..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        {/* Link KPIs */}
        {selectedMemberData?.kpis?.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Link2 className="w-4 h-4 inline mr-1" /> Link KPIs (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {selectedMemberData.kpis.map(kpi => (
                <button
                  key={kpi.id}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      linkedKpis: prev.linkedKpis.includes(kpi.id)
                        ? prev.linkedKpis.filter(id => id !== kpi.id)
                        : [...prev.linkedKpis, kpi.id]
                    }));
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    formData.linkedKpis.includes(kpi.id)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {kpi.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Link Tasks */}
        {memberTasks.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Link2 className="w-4 h-4 inline mr-1" /> Link Tasks (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {memberTasks.map(task => (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      linkedTasks: prev.linkedTasks.includes(task.id)
                        ? prev.linkedTasks.filter(id => id !== task.id)
                        : [...prev.linkedTasks, task.id]
                    }));
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    formData.linkedTasks.includes(task.id)
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {task.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Required</label>
          <input
            type="text"
            value={formData.followUp}
            onChange={(e) => setFormData(prev => ({ ...prev, followUp: e.target.value }))}
            placeholder="Any follow-up actions or next steps..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {engagement?.id ? 'Update' : 'Record'} Engagement
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// KPI SCORING VIEW
// ============================================================

const KPIScoringView = ({ members, scores, onSaveScores, onBack, saving }) => {
  const currentWeek = getCurrentWeekKey();
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [weekScores, setWeekScores] = useState({});

  // Load existing scores for selected week
  useEffect(() => {
    const existingScores = {};
    scores.filter(s => s.weekKey === selectedWeek).forEach(s => {
      existingScores[s.memberId] = s.scores || [];
    });
    setWeekScores(existingScores);
  }, [selectedWeek, scores]);

  const handleScoreChange = (memberId, kpiId, score) => {
    setWeekScores(prev => {
      const memberScores = prev[memberId] || [];
      const existing = memberScores.findIndex(s => s.kpiId === kpiId);
      
      if (existing >= 0) {
        memberScores[existing] = { ...memberScores[existing], score };
      } else {
        memberScores.push({ kpiId, score });
      }
      
      return { ...prev, [memberId]: [...memberScores] };
    });
  };

  const getScore = (memberId, kpiId) => {
    return weekScores[memberId]?.find(s => s.kpiId === kpiId)?.score || 0;
  };

  const handleSave = () => {
    const scoresToSave = Object.entries(weekScores).map(([memberId, memberScores]) => ({
      memberId,
      weekKey: selectedWeek,
      scores: memberScores,
      scoredAt: new Date().toISOString()
    }));
    onSaveScores(scoresToSave);
  };

  const membersWithKpis = members.filter(m => m.kpis?.length > 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Weekly KPI Scoring</h1>
              <p className="text-gray-500">Rate team members on their KPIs</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="week"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Scores
            </button>
          </div>
        </div>

        {/* Scoring Cards */}
        {membersWithKpis.length === 0 ? (
          <EmptyState
            icon={Target}
            title="No KPIs to Score"
            description="Add KPIs to team members to enable weekly scoring"
          />
        ) : (
          <div className="space-y-4">
            {membersWithKpis.map(member => (
              <div key={member.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {/* Member Header */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 border-b border-gray-100">
                  <Avatar src={member.photoUrl} name={member.name} size="md" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{member.name}</h3>
                    <p className="text-sm text-gray-500">{member.role}</p>
                  </div>
                </div>

                {/* KPIs */}
                <div className="p-4 space-y-4">
                  {member.kpis.map(kpi => (
                    <div key={kpi.id} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{kpi.name}</p>
                        <p className="text-sm text-gray-500">Target: {kpi.target} {kpi.unit}</p>
                      </div>
                      
                      {/* Star Rating */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => handleScoreChange(member.id, kpi.id, star)}
                            className="p-1 hover:scale-110 transition-transform"
                          >
                            <Star
                              className={`w-6 h-6 ${
                                star <= getScore(member.id, kpi.id)
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                        <span className="ml-2 text-sm font-medium text-gray-600 w-8">
                          {getScore(member.id, kpi.id) || '-'}/5
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TASKS LIST
// ============================================================

const TasksList = ({ tasks, members, onEdit, onCreate, onDelete, onStatusChange }) => {
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredTasks = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    return tasks.filter(t => t.status === statusFilter);
  }, [tasks, statusFilter]);

  const getMemberName = (memberId) => members.find(m => m.id === memberId)?.name || 'Unknown';

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {['all', ...Object.values(TaskStatus)].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === status
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'All Tasks' : getStatusConfig(status).label}
          </button>
        ))}
      </div>

      {/* Tasks */}
      {filteredTasks.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="No tasks found"
          description={statusFilter === 'all' ? 'Create your first task' : 'No tasks with this status'}
          action={
            <button
              onClick={onCreate}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" /> Create Task
            </button>
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredTasks.map(task => (
            <div key={task.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{task.title}</h3>
                    <PriorityBadge priority={task.priority} />
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{task.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    {task.dueDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {formatDate(task.dueDate)}
                      </span>
                    )}
                    {task.assignees?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {task.assignees.map(getMemberName).join(', ')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={task.status}
                    onChange={(e) => onStatusChange(task.id, e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    {Object.values(TaskStatus).map(status => (
                      <option key={status} value={status}>{getStatusConfig(status).label}</option>
                    ))}
                  </select>
                  <button onClick={() => onEdit(task)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => { if(window.confirm('Delete task?')) onDelete(task.id); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// ENGAGEMENTS LIST
// ============================================================

const EngagementsList = ({ engagements, members, tasks, onEdit, onCreate, onDelete }) => {
  const getMember = (memberId) => members.find(m => m.id === memberId);
  const getTask = (taskId) => tasks.find(t => t.id === taskId);

  return (
    <div className="space-y-4">
      {engagements.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No engagements recorded"
          description="Record your first team engagement"
          action={
            <button
              onClick={onCreate}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" /> Record Engagement
            </button>
          }
        />
      ) : (
        <div className="space-y-3">
          {engagements.map(eng => {
            const member = getMember(eng.memberId);
            const config = getEngagementConfig(eng.type);
            const Icon = config.icon;

            return (
              <div key={eng.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <Avatar src={member?.photoUrl} name={member?.name} size="md" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{member?.name || 'Unknown'}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-${config.color}-100 text-${config.color}-700`}>
                        <Icon className="w-3 h-3" />
                        {config.label}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(eng.date)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{eng.notes}</p>
                    
                    {/* Linked Items */}
                    {(eng.linkedKpis?.length > 0 || eng.linkedTasks?.length > 0) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {eng.linkedKpis?.map(kpiId => {
                          const kpi = member?.kpis?.find(k => k.id === kpiId);
                          return kpi ? (
                            <span key={kpiId} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-lg flex items-center gap-1">
                              <Target className="w-3 h-3" /> {kpi.name}
                            </span>
                          ) : null;
                        })}
                        {eng.linkedTasks?.map(taskId => {
                          const task = getTask(taskId);
                          return task ? (
                            <span key={taskId} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg flex items-center gap-1">
                              <CheckSquare className="w-3 h-3" /> {task.title}
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(eng)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => { if(window.confirm('Delete engagement?')) onDelete(eng.id); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const TeamManagerTool = ({ bookId, customStyling = {}, toolsService, onBack }) => {
  // State
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEngagement, setSelectedEngagement] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersData, tasksData, engagementsData, scoresData] = await Promise.all([
        toolsService.getToolData(bookId, 'team-members'),
        toolsService.getToolData(bookId, 'team-tasks'),
        toolsService.getToolData(bookId, 'team-engagements'),
        toolsService.getToolData(bookId, 'kpi-scores')
      ]);
      setMembers(membersData || []);
      setTasks(tasksData || []);
      setEngagements(engagementsData || []);
      setScores(scoresData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const activeTasks = tasks.filter(t => t.status !== TaskStatus.COMPLETED && t.status !== TaskStatus.CANCELLED).length;
    const completedTasks = tasks.filter(t => t.status === TaskStatus.COMPLETED).length;
    const recentEngagements = engagements.filter(e => {
      const date = new Date(e.date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return date >= weekAgo;
    }).length;

    return { activeTasks, completedTasks, recentEngagements };
  }, [tasks, engagements]);

  // Handlers
  const handleSaveMember = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'team-members', data);
      await loadData();
      setViewState(ViewState.MEMBERS_LIST);
      setSelectedMember(null);
    } catch (error) {
      console.error('Error saving member:', error);
      alert('Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      await toolsService.deleteToolData(bookId, 'team-members', memberId);
      await loadData();
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const handleSaveTask = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'team-tasks', data);
      await loadData();
      setViewState(ViewState.TASKS_LIST);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await toolsService.deleteToolData(bookId, 'team-tasks', taskId);
      await loadData();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleTaskStatusChange = async (taskId, status) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      await handleSaveTask({ ...task, status });
    }
  };

  const handleSaveEngagement = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'team-engagements', data);
      await loadData();
      setViewState(ViewState.ENGAGEMENTS_LIST);
      setSelectedEngagement(null);
      setSelectedMember(null);
    } catch (error) {
      console.error('Error saving engagement:', error);
      alert('Failed to save engagement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEngagement = async (engagementId) => {
    try {
      await toolsService.deleteToolData(bookId, 'team-engagements', engagementId);
      await loadData();
    } catch (error) {
      console.error('Error deleting engagement:', error);
    }
  };

  const handleSaveScores = async (scoresToSave) => {
    setSaving(true);
    try {
      for (const score of scoresToSave) {
        const existing = scores.find(s => s.memberId === score.memberId && s.weekKey === score.weekKey);
        await toolsService.saveToolData(bookId, 'kpi-scores', { ...score, id: existing?.id });
      }
      await loadData();
      alert('Scores saved successfully!');
    } catch (error) {
      console.error('Error saving scores:', error);
      alert('Failed to save scores');
    } finally {
      setSaving(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  // Render based on view state
  switch (viewState) {
    case ViewState.MEMBER_FORM:
      return (
        <MemberForm
          member={selectedMember}
          bookId={bookId}
          onSave={handleSaveMember}
          onCancel={() => { setViewState(ViewState.MEMBERS_LIST); setSelectedMember(null); }}
          saving={saving}
        />
      );

    case ViewState.MEMBER_DETAIL:
      return (
        <MemberDetail
          member={selectedMember}
          tasks={tasks}
          engagements={engagements}
          scores={scores}
          onEdit={(m) => { setSelectedMember(m); setViewState(ViewState.MEMBER_FORM); }}
          onBack={() => { setViewState(ViewState.MEMBERS_LIST); setSelectedMember(null); }}
          onAddTask={(m) => { setSelectedMember(m); setSelectedTask({ assignees: [m.id] }); setViewState(ViewState.TASK_FORM); }}
          onAddEngagement={(m) => { setSelectedMember(m); setViewState(ViewState.ENGAGEMENT_FORM); }}
        />
      );

    case ViewState.TASK_FORM:
      return (
        <TaskForm
          task={selectedTask}
          members={members}
          onSave={handleSaveTask}
          onCancel={() => { setViewState(ViewState.TASKS_LIST); setSelectedTask(null); }}
          saving={saving}
        />
      );

    case ViewState.ENGAGEMENT_FORM:
      return (
        <EngagementForm
          engagement={selectedEngagement}
          members={members}
          tasks={tasks}
          selectedMember={selectedMember}
          onSave={handleSaveEngagement}
          onCancel={() => { setViewState(ViewState.ENGAGEMENTS_LIST); setSelectedEngagement(null); setSelectedMember(null); }}
          saving={saving}
        />
      );

    case ViewState.KPI_SCORING:
      return (
        <KPIScoringView
          members={members}
          scores={scores}
          onSaveScores={handleSaveScores}
          onBack={() => setViewState(ViewState.DASHBOARD)}
          saving={saving}
        />
      );

    case ViewState.MEMBERS_LIST:
      return (
        <div className="h-full overflow-y-auto">
          <div className="max-w-6xl mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
              </div>
              <button
                onClick={() => { setSelectedMember(null); setViewState(ViewState.MEMBER_FORM); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" /> Add Member
              </button>
            </div>

            {members.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No team members yet"
                description="Add your first team member to get started"
                action={
                  <button
                    onClick={() => { setSelectedMember(null); setViewState(ViewState.MEMBER_FORM); }}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <UserPlus className="w-5 h-5" /> Add Member
                  </button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map(member => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    taskCount={tasks.filter(t => t.assignees?.includes(member.id)).length}
                    onView={(m) => { setSelectedMember(m); setViewState(ViewState.MEMBER_DETAIL); }}
                    onEdit={(m) => { setSelectedMember(m); setViewState(ViewState.MEMBER_FORM); }}
                    onDelete={handleDeleteMember}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      );

    case ViewState.TASKS_LIST:
      return (
        <div className="h-full overflow-y-auto">
          <div className="max-w-5xl mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
              </div>
              <button
                onClick={() => { setSelectedTask(null); setViewState(ViewState.TASK_FORM); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create Task
              </button>
            </div>
            <TasksList
              tasks={tasks}
              members={members}
              onEdit={(t) => { setSelectedTask(t); setViewState(ViewState.TASK_FORM); }}
              onCreate={() => { setSelectedTask(null); setViewState(ViewState.TASK_FORM); }}
              onDelete={handleDeleteTask}
              onStatusChange={handleTaskStatusChange}
            />
          </div>
        </div>
      );

    case ViewState.ENGAGEMENTS_LIST:
      return (
        <div className="h-full overflow-y-auto">
          <div className="max-w-5xl mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Engagements</h1>
              </div>
              <button
                onClick={() => { setSelectedEngagement(null); setSelectedMember(null); setViewState(ViewState.ENGAGEMENT_FORM); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Record Engagement
              </button>
            </div>
            <EngagementsList
              engagements={engagements}
              members={members}
              tasks={tasks}
              onEdit={(e) => { setSelectedEngagement(e); setViewState(ViewState.ENGAGEMENT_FORM); }}
              onCreate={() => { setSelectedEngagement(null); setViewState(ViewState.ENGAGEMENT_FORM); }}
              onDelete={handleDeleteEngagement}
            />
          </div>
        </div>
      );

    default: // DASHBOARD
      return (
        <div className="h-full overflow-y-auto">
          <div className="max-w-6xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Team Manager</h1>
                  <p className="text-gray-500">Manage your team, tasks, and performance</p>
                </div>
              </div>
              <button
                onClick={() => setViewState(ViewState.KPI_SCORING)}
                className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 flex items-center gap-2"
              >
                <Star className="w-4 h-4" /> Weekly Scoring
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Team Members"
                value={members.length}
                color="indigo"
                onClick={() => setViewState(ViewState.MEMBERS_LIST)}
              />
              <StatCard
                icon={CheckSquare}
                label="Active Tasks"
                value={stats.activeTasks}
                subValue={`${stats.completedTasks} completed`}
                color="blue"
                onClick={() => setViewState(ViewState.TASKS_LIST)}
              />
              <StatCard
                icon={MessageSquare}
                label="Engagements"
                value={engagements.length}
                subValue={`${stats.recentEngagements} this week`}
                color="emerald"
                onClick={() => setViewState(ViewState.ENGAGEMENTS_LIST)}
              />
              <StatCard
                icon={Target}
                label="KPIs Tracked"
                value={members.reduce((sum, m) => sum + (m.kpis?.length || 0), 0)}
                color="purple"
              />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button
                onClick={() => { setSelectedMember(null); setViewState(ViewState.MEMBER_FORM); }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-indigo-600" />
                </div>
                <span className="font-medium text-gray-900">Add Member</span>
              </button>
              <button
                onClick={() => { setSelectedTask(null); setViewState(ViewState.TASK_FORM); }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ListTodo className="w-5 h-5 text-blue-600" />
                </div>
                <span className="font-medium text-gray-900">Create Task</span>
              </button>
              <button
                onClick={() => { setSelectedEngagement(null); setViewState(ViewState.ENGAGEMENT_FORM); }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="font-medium text-gray-900">Record Engagement</span>
              </button>
              <button
                onClick={() => setViewState(ViewState.KPI_SCORING)}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <span className="font-medium text-gray-900">Score KPIs</span>
              </button>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Members */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Team Members</h2>
                  <button
                    onClick={() => setViewState(ViewState.MEMBERS_LIST)}
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    View All
                  </button>
                </div>
                {members.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No team members yet</p>
                ) : (
                  <div className="space-y-3">
                    {members.slice(0, 4).map(member => (
                      <button
                        key={member.id}
                        onClick={() => { setSelectedMember(member); setViewState(ViewState.MEMBER_DETAIL); }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-left"
                      >
                        <Avatar src={member.photoUrl} name={member.name} size="md" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.role || 'Team Member'}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Engagements */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Recent Engagements</h2>
                  <button
                    onClick={() => setViewState(ViewState.ENGAGEMENTS_LIST)}
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    View All
                  </button>
                </div>
                {engagements.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No engagements recorded</p>
                ) : (
                  <div className="space-y-3">
                    {engagements.slice(0, 4).map(eng => {
                      const member = members.find(m => m.id === eng.memberId);
                      const config = getEngagementConfig(eng.type);
                      return (
                        <div key={eng.id} className="p-3 rounded-xl bg-gray-50">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 text-sm">{member?.name || 'Unknown'}</span>
                            <span className={`text-xs text-${config.color}-600`}>• {config.label}</span>
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-1">{eng.notes}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
  }
};

export default TeamManagerTool;